import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import {
  fetchAddCampaignConcept,
  fetchEditCampaignConcept,
} from 'api/campaignArtApi';
import { artBriefRequestTypes } from 'constants/campaignConstants';
import showSnackbarError from 'utils/showSnackbarError';

const initialState = {
  artId: null,
  campaignId: '',
  concept: [],
  conceptType: '1',
  images: [],
  text: '',
  colors: '',
  phone: '',
  email: '',
  brand: '',
  website: '',
  facebook: '',
  instagram: '',
  twitter: '',
  tiktok: '',
  snapchat: '',
  isLoading: false,
};

export const createArtBrief = createAsyncThunk(
  'newArtBrief/createArtBrief',
  async ({ uploads }, { getState, dispatch, rejectWithValue }) => {
    try {
      const {
        artId,
        campaignId,
        text,
        colors,
        concept,
        conceptType,
        phone,
        email,
        brand,
        website,
        facebook,
        instagram,
        twitter,
        tiktok,
        snapchat,
      } = getState().newArtBrief;

      const requestData = {
        campaignId,
        uploads,
        text: `${artBriefRequestTypes[conceptType]}. ${text}`,
        colors,
        concept,
        phone,
        email,
        brand,
        website,
        facebook,
        instagram,
        twitter,
        tiktok,
        snapchat,
      };

      if (artId) {
        await fetchEditCampaignConcept({
          artId,
          ...requestData,
        });
      } else {
        await fetchAddCampaignConcept(requestData);
      }

      return null;
    } catch (e) {
      if (e.response.data?.error?.message) {
        dispatch(showSnackbarError(e.response.data?.error?.message));
      } else {
        dispatch(showSnackbarError(e.message, e.stack));
      }

      return rejectWithValue(e.message);
    }
  }
);

export const newArtBriefSlice = createSlice({
  name: 'newArtBrief',
  initialState,
  reducers: {
    setBriefConcept(state, action) {
      state.concept = action.payload.concept;
    },
    setBriefConceptType(state, action) {
      state.conceptType = action.payload.conceptType;
    },
    setBriefImages(state, action) {
      state.images = action.payload.images;
    },
    setBriefInfo(state, action) {
      state.campaignId = action.payload.campaignId;
    },
    setBriefText(state, action) {
      state.text = action.payload.text;
      state.colors = action.payload.colors;
      state.phone = action.payload.phone;
      state.email = action.payload.email;
      state.brand = action.payload.brand;
      state.website = action.payload.website;
      state.facebook = action.payload.facebook;
      state.instagram = action.payload.instagram;
      state.twitter = action.payload.twitter;
      state.tiktok = action.payload.tiktok;
      state.snapchat = action.payload.snapchat;
    },
    setBriefDefaultFields(state, action) {
      state.artId = action.payload.artId;
      state.concept = action.payload.concept;
      state.campaignId = action.payload.campaignId;
      state.text = action.payload.text || '';
      state.images = action.payload.images || [];
      state.colors = action.payload.colors || '';
      state.conceptType = action.payload.conceptType;
      state.phone = action.payload.phone || '';
      state.email = action.payload.email || '';
      state.brand = action.payload.brand || '';
      state.website = action.payload.website || '';
      state.facebook = action.payload.facebook || '';
      state.instagram = action.payload.instagram || '';
      state.twitter = action.payload.twitter || '';
      state.tiktok = action.payload.tiktok || '';
      state.snapchat = action.payload.snapchat || '';
    },
    clearNewArtBrief(state) {
      state.artId = null;
      state.campaignId = '';
      state.concept = [];
      state.conceptType = '1';
      state.images = [];
      state.text = '';
      state.colors = '';
      state.phone = '';
      state.email = '';
      state.brand = '';
      state.website = '';
      state.facebook = '';
      state.instagram = '';
      state.twitter = '';
      state.tiktok = '';
      state.snapchat = '';
    },
  },
  extraReducers: (builder) => {
    builder.addCase(createArtBrief.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(createArtBrief.fulfilled, (state) => {
      state.isLoading = false;
    });
    builder.addCase(createArtBrief.rejected, (state) => {
      state.isLoading = false;
    });
  },
});

export const {
  setBriefConcept,
  setBriefConceptType,
  setBriefImages,
  setBriefInfo,
  setBriefText,
  setBriefDefaultFields,
  setUploads,
  clearNewArtBrief,
} = newArtBriefSlice.actions;

export default newArtBriefSlice.reducer;
